import { JSX, useMemo } from 'react'

import { Group, List, Skeleton } from '~/components/component-library'
import { DecodedCalldata } from '~/components/txn-display/calls/DecodedCalldata'
import {
  DecodedCallSummary,
  OfframpDisplayData,
  StateChangeItem,
} from '~/components/txn-display/state-change/types'
import {
  buildStateChangeListItem,
  formatOfframpFeeFooter,
} from '~/components/txn-display/state-change/utils/buildStateChangeList'

type StateChangeListProps = {
  stateChangeItems: StateChangeItem[]
  isLoading?: boolean
  dataRefreshing?: boolean
  footer?: string | JSX.Element
  decodedCalls?: DecodedCallSummary[]
  isShowingCalls: boolean
  secondaryDisplayButtons: JSX.Element | undefined
  offrampData?: OfframpDisplayData
}

export const StateChangeList = ({
  stateChangeItems,
  isLoading = false,
  dataRefreshing = false,
  footer,
  decodedCalls,
  isShowingCalls,
  secondaryDisplayButtons,
  offrampData,
}: StateChangeListProps) => {
  const builtStateChangeListItems = useMemo(
    () =>
      stateChangeItems.map((item) =>
        buildStateChangeListItem(item, dataRefreshing, offrampData),
      ),
    [stateChangeItems, dataRefreshing, offrampData],
  )

  const showStateChangesList =
    !isLoading && builtStateChangeListItems.length > 0
  const showDecodedCalls = isShowingCalls && !!decodedCalls?.length

  const offrampFooter = offrampData ? formatOfframpFeeFooter(offrampData) : null

  // Combine footers - show offramp footer first, then any other footer
  const combinedFooter = useMemo(() => {
    if (offrampFooter && footer) {
      return (
        <div className="flex flex-col gap-2">
          <div>{offrampFooter}</div>
          <div>{footer}</div>
        </div>
      )
    }
    return offrampFooter ?? footer
  }, [offrampFooter, footer])

  return (
    <Group
      label="State change"
      secondaryDisplay={secondaryDisplayButtons}
      footer={combinedFooter}
    >
      {showDecodedCalls ? (
        <DecodedCalldata calls={decodedCalls} />
      ) : isLoading ? (
        <Skeleton className="h-[127px] w-full" />
      ) : (
        showStateChangesList && <List items={builtStateChangeListItems} />
      )}
    </Group>
  )
}
